import * as React from 'react';
import { compose } from 'recompose';
import { OemProps, skin } from 'ui/styles/skin';
import { AutoSizer, List, Index, ListRowProps } from 'react-virtualized';
import { FadeCollapse } from 'ui/transitions';
import { DownTriangle } from 'ui/shapes';

import './TreeView.css';

const ROW_HEIGHT = 25;
const ANIMATION_DURATION = 100;

type StyleProps = {
    style: React.CSSProperties;
};

const ExpandIndicator = ({ style }: StyleProps) =>
    <DownTriangle width=".6em" height=".6em" style={ style }/>
;

const CollapseIndicator = ({ style }: StyleProps) =>
    <DownTriangle width=".6em" height=".6em" style={ style }/>
;

const NoIndicator = ({ style }: StyleProps) =>
    <span style={ style }/>
;

const emptyStyle = {};

type Node<T> = {
    id: string;
    key: string;
    checked: boolean;
    selected: boolean;
    children: Node<T>[];
    item: T;
};
type RenderLabel = <T>(node: Node<T>) => React.ReactNode;

export type OwnProps<T> = {
    id: string;
    data: Node<T>[];
    hideRoot?: boolean;
    onClick: (id: string) => void;
    color: string;
    levelsToAutoExpand: number;
    children: RenderLabel;
};
export type State = {
    expanded: {};
    nodes: {};
    expandIndicatorStyle: React.CSSProperties;
    collapseIndicatorStyle: React.CSSProperties;
    noIndicatorStyle: React.CSSProperties;
};

class RawTreeView<T> extends React.Component<Props<T>, State> {
    // Ref of the List element
    private list: List;

    constructor(props: Props<T>) {
        super(props);
        this.state = {
            expanded: this.calculateInitialExpandedState(props),
            nodes: this.flattenNodes(props),
            expandIndicatorStyle: {
                transform: 'rotate(-90deg)',
                fill: props.color,
                opacity: .5,
            },
            collapseIndicatorStyle: {
                transform: 'rotate(-45deg)',
                fill: props.color,
                opacity: .5,
            },
            noIndicatorStyle: {
                display: 'inline-block',
                width: '.6em',
                height: '.6em',
            },
        };
    }

    calculateInitialExpandedState = (props: OwnProps<T>) => {
        const { data } = props;
        function getState(node: Node<T>, depth: number): {} {
            if (depth <= 0) {
                return {};
            }
            const seed = {
                [node.id]: true,
            };
            return (node.children || []).reduce((_: {}, child: Node<T>) => ({
                ..._,
                ...getState(child, depth - 1),
            }), seed);
        }

        // Expand first n levels
        return data.reduce((_, node) => ({ ..._, ...getState(node, props.levelsToAutoExpand) }), {});
    }

    //
    // flattenNodes takes the tree structure of props.data and puts the nodes into a lookup by id
    // so that we can go from an item ID directly to the node without having to find it in the tree
    //
    flattenNodes = (props: OwnProps<T>) => {
        const { data } = props;
        const queue = [ ...data ];

        const nodes = {};
        let i = 0;
        while (i < queue.length) {
            const node = queue[i++];
            nodes[node.id] = node;
            if (node.children) {
                queue.push(...node.children);
            }
        }
        return nodes;
    }

    componentDidUpdate(oldProps: OwnProps<T>) {
        if (oldProps.data !== this.props.data) {
            const initialExpanded = this.calculateInitialExpandedState(this.props);
            const nodes = this.flattenNodes(this.props);

            this.setState(state => {
                return {
                    expanded: {
                        ...initialExpanded,
                        ...state.expanded,
                    },
                    nodes,
                };
            }, () => {
                this.list.recomputeRowHeights();
            });
        }
        else {
            this.list.recomputeRowHeights();
        }
    }

    setRef = (ref: List) => {
        this.list = ref;
    }

    getExpandedNodeCount = (node: Node<T>): number => {
        if (!node) {
            return 0;
        }
        if (this.state.expanded[node.id]) {
            return node.children
                .map(this.getExpandedNodeCount)
                .reduce((total, count) => total + count, 1);
        }
        return 1;
    }

    rowHeight = (params: Index) => {
        return this.getExpandedNodeCount(this.props.data[params.index]) * ROW_HEIGHT;
    }

    cellRenderer = ({ key, style, index }: ListRowProps) => {
        const item = this.props.data[index];
        return (
            <div key={ key } style={ style }>
                { this.renderNodeAndChildren(item, 0) }
            </div>
        );
    }

    renderNode = (node: Node<T>) => {
        const { styles } = this.props;
        const renderLabel = this.props.children;
        const isExpanded = !!this.state.expanded[node.id];
        const hasChildren = !!node.children.length;
        return (
            <div
                className="item"
                key={ node.id }
                onClick={ this.handleClickNode }
                data-itemid={ node.id }
                style={ node.selected ? styles.Highlighted : emptyStyle }
            >
                { isExpanded && hasChildren &&
                    <CollapseIndicator style={ this.state.collapseIndicatorStyle }/>
                }
                { !isExpanded && hasChildren &&
                    <ExpandIndicator style={ this.state.expandIndicatorStyle }/>
                }
                { !isExpanded && !hasChildren &&
                    <NoIndicator style={ this.state.noIndicatorStyle }/>
                }
                <div className="label">
                    { renderLabel(node) }
                </div>
            </div>
        );
    }

    handleClickNode = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        
        const itemId = event.currentTarget.dataset.itemid || '';
        const { nodes } = this.state;
        const node = nodes[itemId];

        if (node && node.children.length) {
            this.setState(state => ({
                expanded: {
                    ...state.expanded,
                    [node.id]: !state.expanded[node.id],
                },
            }), () => {
                this.list.recomputeRowHeights();
                this.list.forceUpdate();
            });
        }

        this.props.onClick(itemId);
    }

    renderNodeAndChildren = (node: Node<T>, depth: number) => {
        let children: React.ReactNode[] = [];

        if (!node) {
            return null;
        }

        if (this.state.expanded[node.id]) {
            children = node.children.map((child: Node<T>, index: number) =>
                this.renderNodeAndChildren(child, depth + 1)
            );
        }

        if (depth === 0 && this.props.hideRoot) {
            return children;
        }

        return (
            <ul key={ `ul${node.key}` }>
                <li>
                    { this.renderNode(node) }
                    <FadeCollapse
                        visible={ this.state.expanded[node.id] }
                        height={ (this.getExpandedNodeCount(node) - 1) * ROW_HEIGHT }
                        duration={ ANIMATION_DURATION }
                    >
                        { children }
                    </FadeCollapse>
                </li>
            </ul>
        );
    }

    render() {
        const { id } = this.props;
        return (
            <div
                id={ id }
                className="treeview"
            >
                <AutoSizer>
                    { ({ width, height }) =>
                        <List
                            height={ height }
                            overscanRowCount={ 10 }
                            ref={ this.setRef }
                            rowHeight={ this.rowHeight }
                            rowRenderer={ this.cellRenderer }
                            rowCount={ this.props.data.length }
                            width={ width }
                        />
                    }
                </AutoSizer>
            </div>
        );
    }
}

type Props<T> = OwnProps<T> & OemProps;

export const TreeView = compose<Props<{}>, OwnProps<{}>>(
    skin,
)(RawTreeView);



// WEBPACK FOOTER //
// ./src/ui/shared/TreeView.tsx